// AuthProvider — resolves the current identity + view context once, exposes it via context.
// In production the app is loaded inside a GHL iframe; identity arrives via SSO
// (postMessage → /sso/decrypt → /me). For local dev, `?as=agency|customer` yields a
// demo identity from /me directly.
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from '../lib/api';
import type { Me } from '../lib/types';

interface AuthState {
  loading: boolean;
  error: string | null;
  me: Me | null;
}

const AuthContext = createContext<AuthState>({ loading: true, error: null, me: null });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ loading: true, error: null, me: null });

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((me) => { if (!cancelled) setState({ loading: false, error: null, me }); })
      .catch((err: Error) => { if (!cancelled) setState({ loading: false, error: err.message, me: null }); });
    return () => { cancelled = true; };
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
